import { createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../shared/utils/api.js'

export const fetchUser = createAsyncThunk('user/fetch', async (userId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/v1/admin/profile/${userId}`)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message)
  }
})

export const createUser = createAsyncThunk('user/create', async (userData, { rejectWithValue }) => {
  try {
    const response = await api.post('/v1/admin/create', userData)
    return response.data
  } catch (error) {
    // Handle validation errors specially to preserve the structure needed by the form
    if (error.response?.data?.validationErrors) {
      return rejectWithValue(error.response.data)
    }
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create user'
    return rejectWithValue(errorMessage)
  }
})

export const updateUser = createAsyncThunk('user/update', async (userData, { rejectWithValue }) => {
  try {
    const response = await api.patch('/v1/admin/settings/profile', userData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message)
  }
})

export const uploadProfilePhoto = createAsyncThunk('user/uploadPhoto', async (photoFile, { rejectWithValue }) => {
  try {
    const formData = new FormData()
    formData.append('photo', photoFile)

    const response = await api.post('/v1/admin/profile/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message)
  }
})

export const deleteUser = createAsyncThunk('user/delete', async (userId, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/v1/admin/users/${userId}`)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message)
  }
})

export const deactivateAccount = createAsyncThunk(
  'user/deactivateAccount',
  async ({ userId, message }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/admin/deactivate/account/user/${userId}`, {
        reason: message  // Changed from 'message' to 'reason' to match backend
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const blockAccount = createAsyncThunk(
  'user/blockAccount', 
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required')
      }

      const response = await api.post(`/v1/admin/block/account/user/${userId}`)
      
      return response.data
    } catch (error) {
      console.error('Block error:', error)
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to block account'
      )
    }
  }
)

export const unblockAccount = createAsyncThunk(
  'user/unblockAccount', 
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required')
      }

      const response = await api.post(`/v1/admin/unblock/account/user/${userId}`)
      
      return response.data
    } catch (error) {
      console.error('Unblock error:', error)
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to unblock account'
      )
    }
  }
)

// Preferences actions
export const fetchPreferences = createAsyncThunk('user/fetchPreferences', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState()
    const userId = state.auth.user?.id

    if (!userId) {
      return rejectWithValue('User ID not found. Please log in again.')
    }

    const response = await api.get(`/v1/admin/preferences?userId=${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch preferences'
    return rejectWithValue(errorMessage)
  }
})

export const updatePreferences = createAsyncThunk(
  'user/updatePreferences',
  async (preferences, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const userId = state.auth.user?.id

      if (!userId) {
        return rejectWithValue('User ID not found. Please log in again.')
      }

      const response = await api.put(`/v1/admin/preferences?userId=${userId}`, preferences)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update preferences'
      return rejectWithValue(errorMessage)
    }
  }
)

export const confirmPreferences = createAsyncThunk(
  'user/confirmPreferences',
  async ({ confirmationCode, preferences }, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/admin/preferences/confirm', {
        confirmationCode,
        preferences,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to confirm preferences'
      return rejectWithValue(errorMessage)
    }
  }
)

export const resetPreferences = createAsyncThunk(
  'user/resetPreferences',
  async ({ confirmReset, categories }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const userId = state.auth.user?.id

      if (!userId) {
        return rejectWithValue('User ID not found. Please log in again.')
      }

      const response = await api.post(`/v1/admin/preferences/reset?userId=${userId}`, {
        confirmReset,
        categories,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset preferences'
      return rejectWithValue(errorMessage)
    }
  }
)

// Verification actions
export const fetchVerificationStatus = createAsyncThunk(
  'user/fetchVerificationStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/admin/verification-status')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch verification status')
    }
  }
)

export const sendVerifyEmail = createAsyncThunk('user/sendVerifyEmail', async (targetEmail, { rejectWithValue }) => {
  try {
    const payload = targetEmail ? { email: targetEmail } : undefined
    const response = await api.post('/v1/admin/verify-email/send', payload)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to send email verification')
  }
})

export const confirmVerifyEmail = createAsyncThunk('user/confirmVerifyEmail', async (payload, { rejectWithValue }) => {
  try {
    const otp = typeof payload === 'string' ? payload : payload?.otp
    const email = typeof payload === 'object' ? payload?.email : undefined

    const body = email ? { otp, email } : { otp }
    const response = await api.post('/v1/admin/verify-email/confirm', body)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to verify email')
  }
})

export const sendVerifyPhone = createAsyncThunk('user/sendVerifyPhone', async (targetPhone, { rejectWithValue }) => {
  try {
    const payload = targetPhone ? { phone: targetPhone } : undefined
    const response = await api.post('/v1/admin/verify-phone/send', payload)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to send phone verification')
  }
})

export const confirmVerifyPhone = createAsyncThunk('user/confirmVerifyPhone', async (payload, { rejectWithValue }) => {
  try {
    const otp = typeof payload === 'string' ? payload : payload?.otp
    const phone = typeof payload === 'object' ? payload?.phone : undefined
    const body = phone ? { otp, phone } : { otp }
    const response = await api.post('/v1/admin/verify-phone/confirm', body)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to verify phone')
  }
})

export const resetEmailVerification = createAsyncThunk(
  'user/resetEmailVerification',
  async (_, { rejectWithValue }) => {
    try {
      return {}
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const resetPhoneVerification = createAsyncThunk(
  'user/resetPhoneVerification',
  async (_, { rejectWithValue }) => {
    try {
      return {}
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchCreatedAdmins = createAsyncThunk('user/fetchCreatedAdmins', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/v1/admin/created-admins')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch created admins'
    return rejectWithValue(errorMessage)
  }
})

export const fetchAdminProfile = createAsyncThunk('user/fetchAdminProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/v1/admin/profile')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch admin profile'
    return rejectWithValue(errorMessage)
  }
})

export const updateLanguagePreference = createAsyncThunk(
  'user/updateLanguagePreference',
  async (language, { rejectWithValue }) => {
    try {
      const response = await api.put('/v1/admin/settings/language', { language })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update language preference'
      return rejectWithValue(errorMessage)
    }
  }
)